// Empire Apps Chassis · identity-zkp · Wasiyat nominee ZKP
// Wasiyat-specific. Lets the testator prove to a nominee:
//   "you are nominated for asset class X"
// without revealing the full will, the other nominees, or the other assets.
//
// Construction:
//   1. Will JSON has shape: { nominees: [{id, relation, aadhaar_last4, assets: [...]}, ...] }
//   2. For each nominee + asset-class pair, testator signs a per-tuple credential
//   3. Nominee receives only the tuples matching their own DID/Aadhaar-last4
//   4. Nominee can selectively disclose to a notary/court (e.g., on death event)
//
// The will itself is never transmitted. Only per-nominee VPs that prove
// "I am the X nominee for {asset_class, amount_band}" with selective disclosure.

import type {
  Predicate,
  DIDString,
  Ed25519PrivateKey,
  Ed25519PublicKey,
  AttributeValue,
} from './types.ts';
import { bbsSign, type BBSSignedCredential } from './bbs-plus.ts';
import { deriveBundle, type DisclosureBundle } from './selective-disclosure.ts';

export type AssetClassSlug =
  | 'bank_account'
  | 'mutual_fund'
  | 'demat'
  | 'fixed_deposit'
  | 'crypto'
  | 'insurance_life'
  | 'insurance_health'
  | 'insurance_motor'
  | 'property'
  | 'locker'
  | 'jewellery'
  | 'business_share'
  | 'pf_epfo'
  | 'nps'
  | 'ppf'
  | 'digital_domain'
  | 'digital_hosting'
  | 'digital_saas'
  | 'other';

export type NomineeRelation =
  | 'spouse' | 'child' | 'parent' | 'sibling' | 'in_law' | 'lawyer' | 'friend' | 'trust' | 'other';

export interface NomineeBinding {
  readonly nomineeId: string; // opaque uuid, not Aadhaar
  readonly relation: NomineeRelation;
  readonly aadhaarLast4: string;
  readonly assetClass: AssetClassSlug;
  readonly amountBand: 'lt_5L' | '5L_25L' | '25L_1Cr' | 'gt_1Cr' | 'unknown';
  readonly testatorEmpireDid: DIDString;
  readonly mintedAt: string;
}

export interface NomineeZkpAttributes {
  readonly nominee_id_hash: string;
  readonly relation: string;
  readonly aadhaar_last4: string;
  readonly asset_class: string;
  readonly amount_band: string;
  readonly testator_did: string;
  readonly minted_at: string;
}

export async function bindingToZkpAttributes(
  b: NomineeBinding,
): Promise<NomineeZkpAttributes> {
  const idHashBytes = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(b.nomineeId)),
  );
  let hex = '';
  for (const x of idHashBytes) hex += (x as number).toString(16).padStart(2, '0');
  return {
    nominee_id_hash: hex.slice(0, 16),
    relation: b.relation,
    aadhaar_last4: b.aadhaarLast4,
    asset_class: b.assetClass,
    amount_band: b.amountBand,
    testator_did: b.testatorEmpireDid,
    minted_at: b.mintedAt,
  };
}

export async function nomineeBindingSign(
  binding: NomineeBinding,
  testatorSk: Ed25519PrivateKey,
  testatorPk: Ed25519PublicKey,
): Promise<BBSSignedCredential> {
  const attrs = await bindingToZkpAttributes(binding);
  const flat: Record<string, AttributeValue> = {
    nominee_id_hash: attrs.nominee_id_hash,
    relation: attrs.relation,
    aadhaar_last4: attrs.aadhaar_last4,
    asset_class: attrs.asset_class,
    amount_band: attrs.amount_band,
    testator_did: attrs.testator_did,
    minted_at: attrs.minted_at,
  };
  return bbsSign(flat, testatorSk, testatorPk);
}

export const NOMINEE_PREDICATE_LIBRARY = {
  isNomineeOfClass: (cls: AssetClassSlug): Predicate => ({
    attribute: 'asset_class',
    op: 'eq',
    target: cls,
  }),
  relationEq: (r: NomineeRelation): Predicate => ({
    attribute: 'relation',
    op: 'eq',
    target: r,
  }),
  aadhaarLast4Eq: (l4: string): Predicate => ({
    attribute: 'aadhaar_last4',
    op: 'eq',
    target: l4,
  }),
  amountBandIn: (bands: readonly string[]): Predicate => ({
    attribute: 'amount_band',
    op: 'in',
    target: bands,
  }),
  testatorReveal: { attribute: 'testator_did', op: 'reveal', target: '' } as Predicate,
} as const;

export async function nomineeDeriveBundle(
  cred: BBSSignedCredential,
  predicates: readonly Predicate[],
): Promise<DisclosureBundle> {
  return deriveBundle(cred, predicates);
}

// Helper: aggregate a will into per-nominee credentials. Each nominee gets
// one credential per (nominee, asset_class) tuple they appear on.
export interface WillNomineeRow {
  readonly nomineeId: string;
  readonly relation: NomineeRelation;
  readonly aadhaarLast4: string;
  readonly assetClasses: readonly AssetClassSlug[];
  readonly amountBandByClass: Partial<Record<AssetClassSlug, NomineeBinding['amountBand']>>;
}

export async function willToBindings(
  rows: readonly WillNomineeRow[],
  testatorDid: DIDString,
): Promise<readonly NomineeBinding[]> {
  const out: NomineeBinding[] = [];
  const mintedAt = new Date().toISOString();
  for (const row of rows) {
    for (const cls of row.assetClasses) {
      out.push({
        nomineeId: row.nomineeId,
        relation: row.relation,
        aadhaarLast4: row.aadhaarLast4,
        assetClass: cls,
        amountBand: row.amountBandByClass[cls] ?? 'unknown',
        testatorEmpireDid: testatorDid,
        mintedAt,
      });
    }
  }
  return out;
}
